import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCubeQuery } from '@cubejs-client/react';
import UserService from '~/auth/UserService';
import { useAppContext } from '~/AppProviders';
import AnalyticFields from '../../enums/AnalyticFields';
import getDimensions from '../utils/getDimensions';
import getMeasures from '../utils/getMeasures';
import {
  transformCountryGeographyResponse,
  transformSourceMetricsResponse,
} from '../utils/supplyOverviewTransform';
import useDateRange from './useDateRange';
import useViewDictionaryQuery from '~/hooks/useViewDictionaryQuery';
import API from '~/enums/API';

const { VITE_ANALYTICS_CUBE_ACCOUNT_NAME: cubeAccountName } = import.meta.env;

function getCubeErrorMessage(error) {
  if (!error) {
    return '';
  }

  const fallbackMessage = 'Something went wrong while fetching analytics data';
  const rawMessage = error?.message || '';
  const jsonStartIndex = rawMessage.indexOf('{');

  if (jsonStartIndex === -1) {
    return rawMessage || fallbackMessage;
  }

  try {
    const parsedError = JSON.parse(rawMessage.substring(jsonStartIndex));
    return parsedError?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

function getCubeErrorStatus(error) {
  const rawMessage = error?.message || '';
  const jsonStartIndex = rawMessage.indexOf('{');

  if (jsonStartIndex === -1) {
    return null;
  }

  try {
    return JSON.parse(rawMessage.substring(jsonStartIndex))?.status ?? null;
  } catch {
    return null;
  }
}

export default function useSupplyOverview() {
  const dateOptions = useDateRange();
  const { workspaceId } = useAppContext();
  const accountId = `${cubeAccountName}${workspaceId}`;
  const { Day, Source, Request, Impressions, Revenue } = AnalyticFields;
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dateRange = useMemo(
    () => dateOptions.dateValue?.map(date => date?.format('YYYY-MM-DD')),
    [dateOptions.dateValue]
  );
  const shouldRunQuery = Boolean(workspaceId && cubeAccountName && dateRange?.every(Boolean));

  const { transformedData, isFetching: sourceFetching } = useViewDictionaryQuery(API.SOURCE, {
    formatter: data => data.map(({ id, name }) => ({ id, name })),
  });

  const countryQuery = useMemo(
    () => ({
      filters: [
        {
          values: [workspaceId],
          member: `${accountId}.workspace_id`,
          operator: 'equals',
        },
      ],
      timeDimensions: [
        {
          dimension: `${accountId}.${Day.name}`,
          dateRange,
        },
      ],
      dimensions: getDimensions(accountId),
      measures: getMeasures(accountId),
    }),
    [accountId, dateRange, workspaceId, Day.name]
  );

  const sourceQuery = useMemo(
    () => ({
      filters: [
        {
          values: [workspaceId],
          member: `${accountId}.workspace_id`,
          operator: 'equals',
        },
      ],
      timeDimensions: [
        {
          dimension: `${accountId}.${Day.name}`,
          dateRange,
        },
      ],
      dimensions: [`${accountId}.${Source.name}`],
      measures: [
        `${accountId}.${Request.name}`,
        `${accountId}.${Impressions.name}`,
        `${accountId}.${Revenue.name}`,
      ],
    }),
    [
      accountId,
      dateRange,
      workspaceId,
      Day.name,
      Source.name,
      Request.name,
      Impressions.name,
      Revenue.name,
    ]
  );

  const {
    resultSet: countryResultSet,
    isLoading: isCountryLoading,
    error: countryError,
  } = useCubeQuery(countryQuery, {
    castNumerics: true,
    skip: !shouldRunQuery,
    resetResultSetOnChange: true,
  });

  const {
    resultSet: sourceResultSet,
    isLoading: isSourceLoading,
    error: sourceError,
  } = useCubeQuery(sourceQuery, {
    castNumerics: true,
    skip: !shouldRunQuery || sourceFetching,
    resetResultSetOnChange: true,
  });

  useEffect(() => {
    if (getCubeErrorStatus(countryError) === 401 || getCubeErrorStatus(sourceError) === 401) {
      UserService.doLogout();
    }
  }, [countryError, sourceError]);

  const countries = useMemo(
    () => transformCountryGeographyResponse(countryResultSet?.tablePivot?.(), accountId),
    [accountId, countryResultSet]
  );

  const sources = useMemo(
    () =>
      transformSourceMetricsResponse(sourceResultSet?.tablePivot?.(), accountId, transformedData),
    [accountId, sourceResultSet, transformedData]
  );

  const handleSelectedCountryChange = useCallback(country => {
    setSelectedCountry(country);
  }, []);

  return {
    countries,
    sources,
    isCountryLoading,
    isSourceLoading,
    isLoading: isCountryLoading || isSourceLoading,
    countryError: getCubeErrorMessage(countryError),
    sourceError: getCubeErrorMessage(sourceError),
    error: getCubeErrorMessage(countryError || sourceError),
    selectedCountry,
    setSelectedCountry,
    handleSelectedCountryChange,
    ...dateOptions,
  };
}
